
import React, { useMemo } from 'react';
import { keyboardMap } from '@/utils/keyboardUtils';
import { cn } from '@/lib/utils';

interface KeyboardDisplayProps {
  domain: string;
  className?: string;
}

const KeyboardDisplay: React.FC<KeyboardDisplayProps> = ({ domain, className }) => {
  // Normalize domain and extract character frequencies
  const normalizedDomain = domain.toLowerCase();
  
  const charFrequency = useMemo(() => {
    const frequency: Record<string, number> = {};
    
    for (const char of normalizedDomain) {
      frequency[char] = (frequency[char] || 0) + 1;
    }
    
    return frequency;
  }, [normalizedDomain]);
  
  // Define keyboard rows
  const keyboardRows = [
    ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p'],
    ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l'],
    ['z', 'x', 'c', 'v', 'b', 'n', 'm']
  ];
  
  // Get intensity for heat map (based on frequency)
  const getIntensity = (char: string) => {
    const count = charFrequency[char] || 0;
    if (count === 0) return 'bg-gray-200 text-gray-600';
    if (count === 1) return 'bg-blue-200 text-blue-800';
    if (count === 2) return 'bg-blue-300 text-blue-900';
    if (count === 3) return 'bg-blue-400 text-white';
    return 'bg-blue-500 text-white';
  };
  
  // Get finger color
  const getFingerColor = (char: string) => {
    const keyInfo = keyboardMap[char];
    if (!keyInfo) return '';
    
    // Colors for different fingers
    switch (keyInfo.finger) {
      case 'pinky': return 'border-pink-500';
      case 'ring': return 'border-purple-500';
      case 'middle': return 'border-blue-500';
      case 'index': return 'border-green-500';
      case 'thumb': return 'border-yellow-500';
      default: return '';
    }
  };

  return (
    <div className={cn("flex flex-col items-center space-y-2 select-none", className)}>
      {keyboardRows.map((row, rowIndex) => (
        <div 
          key={`row-${rowIndex}`} 
          className="flex space-x-1"
          style={{ paddingLeft: rowIndex * 10 }} // Stagger rows like a real keyboard
        >
          {row.map((char) => {
            const isUsed = normalizedDomain.includes(char);
            return (
              <div 
                key={char}
                className={cn(
                  "w-10 h-10 flex items-center justify-center rounded-md text-sm font-medium border-2",
                  getIntensity(char),
                  getFingerColor(char),
                  isUsed ? 'transform transition-transform hover:scale-110' : ''
                )}
              >
                {char.toUpperCase()}
              </div>
            );
          })}
        </div>
      ))}
      
      {/* Spacebar */}
      <div className="flex space-x-1">
        <div 
          className={cn(
            "w-64 h-8 flex items-center justify-center rounded-md text-xs font-medium border-2 border-yellow-500",
            normalizedDomain.includes(' ') ? 'bg-blue-200 text-blue-800' : 'bg-gray-200 text-gray-600'
          )}
        >
          SPACE
        </div>
      </div>
      
      {/* Legend */}
      <div className="flex items-center justify-center space-x-4 mt-2 text-xs">
        <div className="flex items-center">
          <div className="w-3 h-3 bg-gray-200 rounded mr-1"></div>
          <span>Not Used</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 bg-blue-200 rounded mr-1"></div>
          <span>Used Once</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 bg-blue-300 rounded mr-1"></div>
          <span>Used Twice</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 bg-blue-400 rounded mr-1"></div>
          <span>Used 3+ Times</span>
        </div>
      </div>
      
      <div className="flex items-center justify-center space-x-4 mt-1 text-xs">
        <div className="flex items-center">
          <div className="w-3 h-3 border-2 border-pink-500 rounded mr-1"></div>
          <span>Pinky</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 border-2 border-purple-500 rounded mr-1"></div>
          <span>Ring</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 border-2 border-blue-500 rounded mr-1"></div>
          <span>Middle</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 border-2 border-green-500 rounded mr-1"></div>
          <span>Index</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 border-2 border-yellow-500 rounded mr-1"></div>
          <span>Thumb</span>
        </div>
      </div>
    </div>
  );
};

export default KeyboardDisplay;
